import { useState } from 'react';
import { ClipboardList, Plus, Printer } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCreatePurchaseOrderMutation, useProductsQuery, usePurchaseOrderQuery,
  usePurchaseOrdersQuery, useSuppliersQuery,
  type PurchaseOrder, type PurchaseOrderStatus,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { ALL_TIME, DateRangeFilter, type DateRange } from '@/components/DateRangeFilter';
import { PurchaseSlip } from '@/components/PurchaseSlip';
import { PurchaseOrderDetailModal } from './PurchaseOrderDetailModal';

export const PO_TONE: Record<PurchaseOrderStatus, 'neutral' | 'brand' | 'success' | 'warning'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'brand',
  ORDERED: 'brand',
  PARTIALLY_RECEIVED: 'warning',
  FULLY_RECEIVED: 'success',
  CANCELLED: 'neutral',
};

interface LineDraft {
  variantId: string;
  quantity: string;
  unitCost: string;
}

const emptyLine: LineDraft = { variantId: '', quantity: '', unitCost: '' };

export function PurchaseOrdersPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [slipFor, setSlipFor] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>(ALL_TIME);
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = usePurchaseOrdersQuery({
    from: range.from || undefined,
    to: range.to || undefined,
    page,
    limit: 25,
    status: status || undefined,
  });
  const { data: suppliers } = useSuppliersQuery({ limit: 200, isActive: true });
  const slipOrder = usePurchaseOrderQuery(slipFor ?? '', { skip: !slipFor });
  const { data: products } = useProductsQuery({ limit: 200, status: 'ACTIVE' });
  const [createOrder, createState] = useCreatePurchaseOrderMutation();

  const [supplierId, setSupplierId] = useState('');
  const [shipping, setShipping] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ ...emptyLine }]);
  const [failure, setFailure] = useState<string | null>(null);

  const variantOptions = (products?.items ?? []).flatMap((product) =>
    (product.variants ?? []).map((variant) => ({
      id: variant.id,
      label: `${product.name} — ${variant.sku}`,
      cost: product.costPrice,
    })),
  );

  const filled = lines.filter(
    (line) => line.variantId && Number(line.quantity) > 0 && line.unitCost !== '',
  );
  const estimate =
    filled.reduce((sum, line) => sum + Number(line.quantity) * Number(line.unitCost), 0) +
    Number(shipping || 0);

  const setLine = (index: number, patch: Partial<LineDraft>): void => {
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );
  };

  const resetForm = (): void => {
    setSupplierId('');
    setShipping('');
    setLines([{ ...emptyLine }]);
    setFailure(null);
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      const created = await createOrder({
        supplierId,
        shippingAmount: shipping ? Number(shipping) : undefined,
        lines: filled.map((line) => ({
          variantId: line.variantId,
          quantity: Number(line.quantity),
          unitCost: Number(line.unitCost),
        })),
      }).unwrap();

      setCreating(false);
      resetForm();
      setOpenId(created.id);
    } catch (createError) {
      setFailure(apiErrorMessage(createError, 'That order could not be created'));
    }
  };

  return (
    <>
      <PageHeader
        title="Purchase orders"
        description="What you have ordered from suppliers, and what is still outstanding."
        action={
          <Can permission={P.PURCHASE_CREATE}>
            <Button
              icon={Plus}
              onClick={() => setCreating(true)}
              disabled={(suppliers?.items.length ?? 0) === 0}
              title={suppliers?.items.length === 0 ? 'Add a supplier first' : undefined}
            >
              New order
            </Button>
          </Can>
        }
      />

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Orders', value: String(data.summary.count), hint: 'in this period' },
            {
              label: 'Ordered',
              value: formatMoney(String(data.summary.ordered), format),
              hint: 'committed to suppliers',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
          <div className="w-full sm:w-56">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SUBMITTED">Awaiting approval</option>
              <option value="APPROVED">Approved</option>
              <option value="ORDERED">Placed</option>
              <option value="PARTIALLY_RECEIVED">Part received</option>
              <option value="FULLY_RECEIVED">Fully received</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={status ? 'Nothing with that status' : 'No purchase orders yet'}
            description="Raise an order to buy stock from a supplier."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Supplier</Th>
                  <Th>Raised</Th>
                  <Th numeric>Lines</Th>
                  <Th numeric>Total</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((order: PurchaseOrder) => (
                  <tr key={order.id} className="hover:bg-sunken">
                    <Td>
                      <button
                        type="button"
                        className="font-medium hover:underline"
                        onClick={() => setOpenId(order.id)}
                      >
                        {order.number}
                      </button>
                    </Td>
                    <Td className="text-secondary">{order.supplier.name}</Td>
                    <Td className="text-secondary">{formatDate(order.createdAt, format)}</Td>
                    <Td numeric className="text-secondary">{order._count?.items ?? 0}</Td>
                    <Td numeric>{formatMoney(order.total, format)}</Td>
                    <Td>
                      <Badge tone={PO_TONE[order.status]}>
                        {order.status.toLowerCase().replace(/_/g, ' ')}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Printer}
                        onClick={() => setSlipFor(order.id)}
                      >
                        Slip
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => {
          setCreating(false);
          resetForm();
        }}
        title="New purchase order"
        description="Created as a draft. Nothing is committed until it is approved and placed."
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={!supplierId || filled.length === 0}
            >
              Create draft
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {failure && <Alert tone="danger">{failure}</Alert>}

          <Select
            label="Supplier"
            required
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
          >
            <option value="">Select a supplier</option>
            {suppliers?.items.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name} ({supplier.code})
              </option>
            ))}
          </Select>

          <div>
            <p className="mb-2 text-sm font-medium">Lines</p>
            <div className="space-y-3">
              {lines.map((line, index) => (
                <div key={index} className="flex flex-wrap items-end gap-3">
                  <div className="min-w-44 flex-1">
                    <Select
                      label={index === 0 ? 'Product' : undefined}
                      value={line.variantId}
                      onChange={(event) => {
                        const option = variantOptions.find((o) => o.id === event.target.value);
                        setLine(index, {
                          variantId: event.target.value,
                          // Seed with the last cost we carried, which is the
                          // usual starting point for negotiating this order.
                          unitCost: line.unitCost || String(option?.cost ?? ''),
                        });
                      }}
                    >
                      <option value="">Select a product</option>
                      {variantOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="w-24">
                    <Input
                      label={index === 0 ? 'Qty' : undefined}
                      type="number"
                      min="0"
                      step="0.0001"
                      value={line.quantity}
                      onChange={(event) => setLine(index, { quantity: event.target.value })}
                    />
                  </div>
                  <div className="w-28">
                    <Input
                      label={index === 0 ? 'Unit cost' : undefined}
                      type="number"
                      min="0"
                      step="0.0001"
                      value={line.unitCost}
                      onChange={(event) => setLine(index, { unitCost: event.target.value })}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={lines.length === 1}
                    onClick={() =>
                      setLines((current) => current.filter((_, position) => position !== index))
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>

            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => setLines((current) => [...current, { ...emptyLine }])}
            >
              Add line
            </Button>
          </div>

          <div className="flex items-end justify-between gap-4 border-t border-subtle pt-3">
            <div className="w-40">
              <Input
                label="Shipping"
                type="number"
                min="0"
                step="0.01"
                value={shipping}
                onChange={(event) => setShipping(event.target.value)}
              />
            </div>
            <p className="text-sm text-secondary">
              Estimated total{' '}
              <span className="font-semibold text-primary">
                {formatMoney(estimate.toFixed(2), format)}
              </span>
              <span className="ml-2 text-xs text-muted">tax applied on save</span>
            </p>
          </div>
        </div>
      </Modal>

      {slipFor && (
        <PurchaseSlip
          order={slipOrder.data}
          loading={slipOrder.isLoading}
          onClose={() => setSlipFor(null)}
        />
      )}

      {openId && (
        <PurchaseOrderDetailModal orderId={openId} onClose={() => setOpenId(null)} />
      )}
    </>
  );
}
